import { TooltipDirective } from '../../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../../componentes/botones';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  NgZone,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  inject,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  IReporteLuminariaWellness,
  ILuminaria,
  IFilter,
  IQueryParam,
  TipoDispositivoLorawan,
  IReporteGenerico,
  IReporteLuminariaGPEEnergia,
  IReporteLuminariaACTISEnergia,
} from 'modelos/src';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { ChartColors } from '../../../../../../auxiliares/servicios/chart-colors.service';
import { GraficoExpandidoDialogComponent } from './grafico-expandido-dialog/grafico-expandido-dialog.component';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-detalles-luminaria-grafico',
  templateUrl: './detalles-luminaria-grafico.component.html',
  styleUrls: ['./detalles-luminaria-grafico.component.scss'], // corregido styleUrls
  changeDetection: ChangeDetectionStrategy.OnPush, // Optimización crítica para performance
  imports: [
    TooltipDirective,
    BOTONES,
    HighchartsChartModule,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    ReactiveFormsModule,
    NgSelectModule,
  ],
})
export class DetallesLuminariaGraficoComponent implements OnInit, OnDestroy {
  private formularios = inject(FormularioDialogService);
  private lista2 = inject(Lista2Service);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  Highcharts = Highcharts;
  chartRef?: Highcharts.Chart;

  @Input() luminaria: ILuminaria;
  public tipo?: TipoDispositivoLorawan;

  @Output() rangoFechaSeleccionado = new EventEmitter<{
    inicio: Date;
    fin: Date;
  }>();

  public rangoSeleccionado?: { inicio: Date; fin: Date };

  // Throttling para syncCharts - más agresivo
  private syncAnimationFrameId?: number;
  private isSyncing = false;
  private lastSyncTime = 0;
  private readonly SYNC_THROTTLE_MS = 50; // Máximo 20 FPS (1000ms / 20 = 50ms)
  private readonly ENABLE_CHART_SYNC = true; // Mantener sync entre gráficos (feature requerido)

  // Caché para mejorar búsqueda de puntos
  private lastXValue?: number;
  private lastFoundPoints: (Highcharts.Point | null)[] = [];

  //Reportes de dispositivos (útil para Wellness)
  public reportesHistoricoDispositivos: IReporteGenerico[] = [];
  public reportesHistoricoDispositivos$?: Subscription;
  public totalCountReporteDispositivo = 0;

  //Reportes de consumo GPE
  public reportesConsumoGPE: IReporteGenerico[] = [];
  public reportesConsumoGPE$?: Subscription;
  public totalCountReporteConsumoGPE = 0;

  //Reportes de consumo ACTIS
  public reportesConsumoACTIS: IReporteGenerico[] = [];
  public reportesConsumoACTIS$?: Subscription;
  public totalCountReporteConsumoACTIS = 0;

  horasSeleccionadas: number = 5; // valor inicial

  public chartOptionsList: Highcharts.Options[] = [];
  public chartKeys: string[] = [];
  public chartRefs: Highcharts.Chart[] = [];
  public rango: { start: Date; end: Date } = { start: null, end: null };
  public opcionesFechas = [
    { label: 'Último día', value: 1 },
    { label: 'Últimos 5 días', value: 5 },
    { label: 'Últimos 15 días', value: 15 },
    { label: 'Último mes', value: 30 },
    { label: 'Últimos 5 meses', value: 150 },
    { label: 'Último año', value: 365 },
  ];

  // WELLNESS
  public mapWellnessFechaValores = new Map<string, IReporteLuminariaWellness>();
  public KeysWellness: (keyof IReporteLuminariaWellness)[] = [
    'voltage',
    'current',
    'temperature',
    'lumenes',
  ];
  public selectedKeysWellness: (keyof IReporteLuminariaWellness)[] = [
    ...this.KeysWellness,
  ];
  public seriesWellnessNames: Record<string, string> = {
    voltage: 'Voltaje',
    current: 'Corriente',
    temperature: 'Temperatura',
    lumenes: 'Lúmenes',
  };
  public yLabelsWellness: Record<string, string> = {
    voltage: 'Volts',
    current: 'Amper',
    temperature: '°C',
    lumenes: 'Lux',
  };

  // GPE
  public mapGPEFechaValores = new Map<string, IReporteLuminariaGPEEnergia>();
  public KeysGPE: (keyof IReporteLuminariaGPEEnergia)[] = [
    'corriente',
    'voltaje',
    'potencia',
    'energiaTotal',
  ];
  public selectedKeysGPE: (keyof IReporteLuminariaGPEEnergia)[] = [
    ...this.KeysGPE,
  ];
  public seriesGPENames: Record<string, string> = {
    voltaje: 'Voltaje (V)',
    potencia: 'Potencia (W)',
    energiaTotal: 'Energía (kWh)',
    corriente: 'Corriente (mA)',
  };
  public yLabelsGPE: Record<string, string> = {
    voltaje: 'Volts',
    corriente: 'mA',
    potencia: 'W',
    energiaTotal: 'kWh',
  };

  // ACTIS
  public mapACTISFechaValores = new Map<
    string,
    IReporteLuminariaACTISEnergia
  >();
  public KeysACTIS: (keyof IReporteLuminariaACTISEnergia)[] = [
    'corriente',
    'voltaje',
    'potencia',
  ];
  public selectedKeysACTIS: (keyof IReporteLuminariaACTISEnergia)[] = [
    ...this.KeysACTIS,
  ];
  public seriesACTISNames: Record<string, string> = {
    voltaje: 'Voltaje (V)',
    potencia: 'Potencia (W)',
    corriente: 'Corriente (mA)',
  };
  public yLabelsACTIS: Record<string, string> = {
    voltaje: 'Volts',
    corriente: 'mA',
    potencia: 'W',
  };

  /** Las series que se pueden graficar según el tipo de luminaria. */
  public get claves(): string[] {
    if (this.tipo === 'Luminaria Wellness') return this.KeysWellness;
    if (this.tipo === 'Luminaria GPE') return this.KeysGPE;
    if (this.tipo === 'Luminaria ACTIS FING') return this.KeysACTIS;
    return [];
  }

  public seleccionada(key: string): boolean {
    if (this.tipo === 'Luminaria Wellness') {
      return this.selectedKeysWellness.includes(
        key as keyof IReporteLuminariaWellness,
      );
    }
    if (this.tipo === 'Luminaria GPE') {
      return this.selectedKeysGPE.includes(
        key as keyof IReporteLuminariaGPEEnergia,
      );
    }
    if (this.tipo === 'Luminaria ACTIS FING') {
      return this.selectedKeysACTIS.includes(
        key as keyof IReporteLuminariaACTISEnergia,
      );
    }
    return false;
  }

  public nombreSerie(key: string): string {
    const nombres =
      this.tipo === 'Luminaria Wellness'
        ? this.seriesWellnessNames
        : this.tipo === 'Luminaria GPE'
          ? this.seriesGPENames
          : this.seriesACTISNames;
    return nombres[key] || key;
  }

  public toggleKeySelection(key: any) {
    if (this.tipo === 'Luminaria Wellness') {
      const wellnessKey = key as keyof IReporteLuminariaWellness;
      const index = this.selectedKeysWellness.indexOf(wellnessKey);
      if (index > -1) {
        this.selectedKeysWellness = this.selectedKeysWellness.filter(
          (k) => k !== wellnessKey,
        );
      } else {
        this.selectedKeysWellness = [...this.selectedKeysWellness, wellnessKey];
      }
    }
    if (this.tipo === 'Luminaria GPE') {
      const gpeKey = key as keyof IReporteLuminariaGPEEnergia;
      const index = this.selectedKeysGPE.indexOf(gpeKey);
      if (index > -1) {
        this.selectedKeysGPE = this.selectedKeysGPE.filter((k) => k !== gpeKey);
      } else {
        this.selectedKeysGPE = [...this.selectedKeysGPE, gpeKey];
      }
    }
    if (this.tipo === 'Luminaria ACTIS FING') {
      const actisKey = key as keyof IReporteLuminariaACTISEnergia;
      const index = this.selectedKeysACTIS.indexOf(actisKey);
      if (index > -1) {
        this.selectedKeysACTIS = this.selectedKeysACTIS.filter(
          (k) => k !== actisKey,
        );
      } else {
        this.selectedKeysACTIS = [...this.selectedKeysACTIS, actisKey];
      }
    }

    this.hacerGraficosPorKey();
    this.cdr.detectChanges(); // Forzar detección con OnPush
  }

  private buildChartOptions(options: Highcharts.Options): Highcharts.Options {
    // Solo construir si existen chartOptions
    if (!options || Object.keys(options).length === 0) {
      return null;
    }

    const fontFamily = ChartColors.fontFamily();
    const textColor = ChartColors.textColor();
    // El de la superficie, para el tooltip (el gráfico va sin fondo propio)
    const backgroundColor = ChartColors.backgroundColor();
    const gridColor = ChartColors.gridColor();
    const titleColor = ChartColors.titleColor();
    const subtitleColor = ChartColors.subtitleColor();
    const axisLabelColor = ChartColors.axisLabelColor();
    const legendTextColor = ChartColors.legendTextColor();

    // Opciones por defecto con estilos adaptables al tema
    // Sin fondo propio: el gráfico va sobre el panel de la tarjeta (y en el
    // diálogo expandido, sobre su superficie).
    const defaultOptions: Highcharts.Options = {
      chart: {
        backgroundColor: 'transparent',
        style: {
          fontFamily: fontFamily,
        },
      },
      accessibility: {
        enabled: false,
      },
      title: {
        text: undefined, // No mostrar título por defecto
        style: {
          color: titleColor,
          fontFamily: fontFamily,
        },
      },
      subtitle: {
        text: undefined, // No mostrar subtítulo por defecto
        style: {
          color: subtitleColor,
          fontFamily: fontFamily,
        },
      },
      xAxis: {
        labels: {
          style: {
            color: axisLabelColor,
            fontFamily: fontFamily,
          },
        },
        gridLineColor: gridColor,
        lineColor: gridColor,
        tickColor: gridColor,
      },
      yAxis: {
        labels: {
          style: {
            color: axisLabelColor,
            fontFamily: fontFamily,
          },
        },
        gridLineColor: gridColor,
        lineColor: gridColor,
        tickColor: gridColor,
      },
      legend: {
        itemStyle: {
          color: legendTextColor,
          fontFamily: fontFamily,
        },
        itemHoverStyle: {
          color: textColor,
        },
      },
      tooltip: {
        backgroundColor: backgroundColor,
        style: {
          color: textColor,
          fontFamily: fontFamily,
        },
      },
      plotOptions: {
        series: {
          dataLabels: {
            style: {
              color: textColor,
              fontFamily: fontFamily,
              textOutline: 'none',
            },
          },
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false, // Desactivar opciones de exportación por defecto
      },
    };

    // Merge de opciones: defaultOptions + chartOptions del input
    const merged = this.deepMerge(defaultOptions, options);
    return merged;
  }
  private deepMerge(target: any, source: any): any {
    const output = { ...target };

    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach((key) => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }

    return output;
  }
  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  public hacerGraficosPorKey() {
    let selectedKeys: (
      | keyof IReporteLuminariaWellness
      | keyof IReporteLuminariaGPEEnergia
      | keyof IReporteLuminariaACTISEnergia
    )[] = [];
    let mapFechaValores = new Map<
      string,
      | IReporteLuminariaWellness
      | IReporteLuminariaGPEEnergia
      | IReporteLuminariaACTISEnergia
    >();
    let seriesNames: Record<string, string> = {};
    let colorMap: Record<string, string> = {};
    let yLabels: Record<string, string> = {};
    if (this.tipo === 'Luminaria Wellness') {
      selectedKeys = this.selectedKeysWellness; // <- solo las seleccionadas
      mapFechaValores = this.mapWellnessFechaValores;
      seriesNames = this.seriesWellnessNames;
      yLabels = this.yLabelsWellness;
      colorMap = {
        voltage: 'rgb(31, 119, 180)',
        current: 'rgb(255, 127, 14)',
        temperature: 'rgb(160, 44, 44)',
        lumenes: 'rgb(214, 196, 39)',
      };
    } else if (this.tipo === 'Luminaria GPE') {
      selectedKeys = this.selectedKeysGPE; // <- solo las seleccionadas
      mapFechaValores = this.mapGPEFechaValores;
      seriesNames = this.seriesGPENames;
      yLabels = this.yLabelsGPE;
      colorMap = {
        corriente: 'rgb(255, 127, 14)',
        voltaje: 'rgb(31, 119, 180)',
        potencia: 'rgb(197, 19, 19)',
        energia: 'rgb(54, 214, 39)',
      };
    } else if (this.tipo === 'Luminaria ACTIS FING') {
      selectedKeys = this.selectedKeysACTIS;
      mapFechaValores = this.mapACTISFechaValores;
      seriesNames = this.seriesACTISNames;
      yLabels = this.yLabelsACTIS;
      colorMap = {
        corriente: 'rgb(255, 127, 14)',
        voltaje: 'rgb(31, 119, 180)',
        potencia: 'rgb(197, 19, 19)',
      };
    }

    const _charts: Highcharts.Options[] = selectedKeys.map((key) => {
      const serieData: [number, number][] = [];

      for (const [fechaStr, valores] of mapFechaValores.entries()) {
        const time = new Date(fechaStr).getTime();
        if (isNaN(time)) continue; // <-- ignorar fechas inválidas

        let valor: number | undefined;

        if (this.tipo === 'Luminaria Wellness') {
          const valoresPorTipo = valores as IReporteLuminariaWellness;
          valor = valoresPorTipo[
            key as keyof IReporteLuminariaWellness
          ] as number;
        } else if (this.tipo === 'Luminaria GPE') {
          const valoresPorTipo = valores as IReporteLuminariaGPEEnergia;
          valor = valoresPorTipo[
            key as keyof IReporteLuminariaGPEEnergia
          ] as number;
        } else if (this.tipo === 'Luminaria ACTIS FING') {
          const valoresPorTipo = valores as IReporteLuminariaACTISEnergia;
          valor = valoresPorTipo[
            key as keyof IReporteLuminariaACTISEnergia
          ] as number;
        }

        if (typeof valor === 'number') {
          serieData.push([time, valor]);
        }
      }

      const options: Highcharts.Options & { customOptions: any } = {
        customOptions: {
          title: seriesNames[key],
          icon: 'bar_chart',
        },
        chart: {
          height: 300,
          animation: false, // Deshabilitar animación de gráfico
          reflow: false, // Deshabilitar reflow automático para mejor performance
        },
        time: { useUTC: false },
        plotOptions: {
          series: {
            animation: false, // Deshabilitar animación de series
            enableMouseTracking: true,
            stickyTracking: false, // Mejorar rendimiento del tracking
            turboThreshold: 1000, // Optimización para datasets grandes
            shadow: false, // Deshabilitar sombras (crea 3 elementos por shape)
            marker: {
              enabled: false, // Deshabilitar markers para mejor performance
              states: {
                hover: {
                  enabled: true, // Mostrar marker solo en hover
                  radius: 4,
                },
              },
            },
            states: {
              inactive: {
                opacity: 1, // No cambiar opacidad (ahorra cálculos)
              },
            },
          },
        },
        yAxis: {
          title: {
            text: yLabels[key],
          },
        },
        tooltip: {
          xDateFormat: '%d/%m/%Y %H:%M:%S',
          shared: false,
          animation: false, // Deshabilitar animación de tooltip
          hideDelay: 0, // Sin delay al ocultar
          useHTML: false, // Más rápido sin HTML
        },
        xAxis: {
          type: 'datetime',
          title: {
            text: 'Fecha',
          },
          dateTimeLabelFormats: {
            millisecond: '%H:%M:%S',
            second: '%H:%M:%S',
            minute: '%H:%M',
            hour: '%H:%M',
            day: '%d/%m/%Y',
            week: '%d/%m/%Y',
            month: '%m/%Y',
            year: '%Y',
          },
          crosshair: {
            width: 1, // Línea más delgada = menos rendering
          },
        },
        legend: { enabled: false },
        series: [
          {
            type: 'line',
            name: seriesNames[key],
            data: serieData,
            color: colorMap[key],
            lineWidth: 2, // Línea definida
            states: {
              hover: {
                lineWidthPlus: 0, // No aumentar grosor en hover (ahorra rendering)
              },
            },
          },
        ],
        exporting: { enabled: false },
        boost: {
          // Activar boost module para grandes datasets
          useGPUTranslations: true,
          usePreallocated: true,
        },
      };

      return options;
    });

    const charts = _charts.map((options) => {
      return this.buildChartOptions(options);
    });

    this.chartOptionsList = charts;
    this.chartKeys = selectedKeys as string[];

    // Forzar detección de cambios con OnPush
    this.cdr.detectChanges();
  }

  public abrirGrafico(key: string): void {
    let colorMap: Record<string, string> = {};
    let yLabels: Record<string, string> = {};
    let seriesNames: Record<string, string> = {};

    if (this.tipo === 'Luminaria Wellness') {
      colorMap = {
        voltage: 'rgb(31, 119, 180)',
        current: 'rgb(255, 127, 14)',
        temperature: 'rgb(160, 44, 44)',
        lumenes: 'rgb(214, 196, 39)',
      };
      yLabels = this.yLabelsWellness;
      seriesNames = this.seriesWellnessNames;
    } else if (this.tipo === 'Luminaria GPE') {
      colorMap = {
        corriente: 'rgb(255, 127, 14)',
        voltaje: 'rgb(31, 119, 180)',
        potencia: 'rgb(197, 19, 19)',
        energia: 'rgb(54, 214, 39)',
      };
      yLabels = this.yLabelsGPE;
      seriesNames = this.seriesGPENames;
    } else if (this.tipo === 'Luminaria ACTIS FING') {
      colorMap = {
        corriente: 'rgb(255, 127, 14)',
        voltaje: 'rgb(31, 119, 180)',
        potencia: 'rgb(197, 19, 19)',
      };
      yLabels = this.yLabelsACTIS;
      seriesNames = this.seriesACTISNames;
    }

    this.formularios.abrir(GraficoExpandidoDialogComponent, {
      tamano: 'grande',
      data: {
        luminaria: this.luminaria,
        key,
        titulo: seriesNames[key] ?? key,
        tipo: this.tipo,
        colorMap,
        yLabels,
      },
      cerrarAfuera: true,
    });
  }

  public setChartRef(index: number, chart: Highcharts.Chart) {
    this.chartRefs[index] = chart;
    this.chartRef = chart;
  }

  /**
   * El ancho de cada gráfico sigue al de su panel. Con reflow: false (por
   * rendimiento) Highcharts no se entera de los cambios de la grilla: al
   * cargar quedaba más ancho que el panel (recortado) y al pasar a 2 × 2,
   * más angosto (con un hueco al costado).
   */
  private observadorTamano?: ResizeObserver;
  private reajustePendiente = new Set<Highcharts.Chart>();

  private ajustarAlPanel(chart: Highcharts.Chart): void {
    const panel = chart?.container?.parentElement?.parentElement;
    if (!panel) return;
    this.observadorTamano ??= new ResizeObserver((entradas) => {
      for (const entrada of entradas) {
        const grafico = this.chartRefs.find(
          (c) => c?.container?.parentElement?.parentElement === entrada.target,
        );
        if (grafico) this.reajustePendiente.add(grafico);
      }
      requestAnimationFrame(() => {
        this.reajustePendiente.forEach((c) => {
          const ancho = c.container?.parentElement?.clientWidth;
          if (ancho && Math.abs(ancho - c.chartWidth) > 1) {
            c.setSize(ancho, undefined, false);
          }
        });
        this.reajustePendiente.clear();
      });
    });
    this.observadorTamano.observe(panel);
  }

  public onChartInstance(index: number, chart: Highcharts.Chart) {
    this.setChartRef(index, chart);

    // Ejecutar eventos de mouse FUERA de Angular Zone para evitar change detection
    this.ngZone.runOutsideAngular(() => {
      this.ajustarAlPanel(chart);
      const container = chart?.container;
      if (!container) return;
      container.addEventListener('mousemove', (event: MouseEvent) => {
        this.syncCharts(event, index);
      });

      container.addEventListener('mouseleave', () => {
        this.hideTooltips();
      });
    });
  }

  public syncCharts(event: MouseEvent, sourceChartIndex: number) {
    // Si la sincronización está desactivada, no hacemos nada
    if (!this.ENABLE_CHART_SYNC) return;

    // Throttling: ignorar eventos si ocurrió uno muy recientemente
    const now = performance.now();
    if (now - this.lastSyncTime < this.SYNC_THROTTLE_MS) return;

    // Cancelar cualquier animación pendiente
    if (this.syncAnimationFrameId)
      cancelAnimationFrame(this.syncAnimationFrameId);

    // Si ya estamos sincronizando, evitar reentradas
    if (this.isSyncing) return;

    // RequestAnimationFrame para ejecutar la sincronización de forma eficiente en el próximo frame
    this.syncAnimationFrameId = requestAnimationFrame(() => {
      this.lastSyncTime = performance.now();
      this.isSyncing = true;

      // Obtener el gráfico fuente (el que generó el evento del mouse)
      const sourceChart = this.chartRefs[sourceChartIndex];
      if (!sourceChart) {
        this.isSyncing = false;
        return;
      }

      // Normalizar la posición del mouse respecto al gráfico y convertirla a valor X
      const normalized = sourceChart.pointer.normalize(event);
      const xValue = sourceChart.xAxis[0].toValue(
        normalized.chartX - sourceChart.plotLeft,
        true,
      );

      // Si es el mismo X que el evento anterior y tenemos puntos cacheados,
      // reutilizamos la información para no recalcular
      if (
        this.lastXValue === xValue &&
        this.lastFoundPoints.length === this.chartRefs.length
      ) {
        this.chartRefs.forEach((chart, index) => {
          const point = this.lastFoundPoints[index];
          if (point) {
            // Actualizar tooltip y crosshair en cada gráfico usando la caché
            const chartX = chart.xAxis[0].toPixels(point.x, true);
            chart.tooltip.refresh(point);
            chart.xAxis[0].drawCrosshair({ chartX, chartY: 0 } as any, point);
          } else {
            chart.tooltip.hide();
            chart.xAxis[0].hideCrosshair();
          }
        });
        this.isSyncing = false;
        return;
      }

      // Buscar el punto más cercano en el gráfico fuente
      const sourceSeries = sourceChart.series[0];
      let closestSourcePoint = sourceSeries.data[0];
      let minDiff = Math.abs(sourceSeries.data[0].x - xValue);
      for (let i = 1; i < sourceSeries.data.length; i++) {
        const diff = Math.abs(sourceSeries.data[i].x - xValue);
        if (diff < minDiff) {
          minDiff = diff;
          closestSourcePoint = sourceSeries.data[i];
        }
      }
      const syncX = closestSourcePoint.x; // X a sincronizar en todos los gráficos

      // Actualizar caché de la posición X y los puntos encontrados
      this.lastXValue = xValue;
      this.lastFoundPoints = [];

      // Sincronizar todos los gráficos
      this.chartRefs.forEach((chart, index) => {
        if (!chart) {
          this.lastFoundPoints[index] = null;
          return;
        }

        const series = chart.series[0];
        // Buscar el punto exacto en la serie que tenga el mismo X que el gráfico fuente
        const point = series.data.find((p) => p.x === syncX);

        this.lastFoundPoints[index] = point || null;

        if (point) {
          // Actualizar tooltip y crosshair en este gráfico
          const chartX = chart.xAxis[0].toPixels(point.x, true);
          chart.tooltip.refresh(point);
          chart.xAxis[0].drawCrosshair({ chartX, chartY: 0 } as any, point);
        } else {
          // Si no hay punto para ese X, ocultar tooltip y crosshair
          chart.tooltip.hide();
          chart.xAxis[0].hideCrosshair();
        }
      });

      this.isSyncing = false; // Liberar flag de sincronización
    });
  }

  public hideTooltips() {
    // Si la sincronización está desactivada, no hacer nada
    if (!this.ENABLE_CHART_SYNC) {
      return;
    }
    // Cancelar cualquier sincronización pendiente
    if (this.syncAnimationFrameId) {
      cancelAnimationFrame(this.syncAnimationFrameId);
      this.syncAnimationFrameId = undefined;
    }

    // Limpiar caché
    this.lastXValue = undefined;
    this.lastFoundPoints = [];

    this.chartRefs.forEach((chart) => {
      if (!chart) return;
      chart.tooltip.hide();
      chart.xAxis[0].hideCrosshair();
    });
  }

  //Estos reportes en el caso de la luminaria Wellnes nos brindan datos de consumo
  public async listarReportesDispositivos(): Promise<void> {
    return new Promise<void>((resolve) => {
      const filter: IFilter<IReporteGenerico> = {
        idsAsignados: this.luminaria?._id, //Acá busca que en idsAsignados esté el id de la luminaria
      };
      if (this.rangoSeleccionado?.inicio && this.rangoSeleccionado?.fin) {
        (filter as any)['valores.timestamp'] = {
          $gte: this.rangoSeleccionado.inicio.toISOString(),
          $lte: this.rangoSeleccionado.fin.toISOString(),
        };
      }
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-valores.timestamp',
        limit: 500,
        includeChildren: true,
      };

      //
      this.reportesHistoricoDispositivos$?.unsubscribe();
      this.reportesHistoricoDispositivos$ = this.lista2
        .reportesGenericos(['Luminaria Wellness'], query, 30)
        .subscribe((data) => {
          this.totalCountReporteDispositivo = data.totalCount;
          this.reportesHistoricoDispositivos = data.datos;
          this.cdr.detectChanges(); // Forzar detección con OnPush
          resolve();
        });
    });
  }

  //Estos reportes en el caso de la luminaria GPE nos brindan datos de consumo
  public async listarReportesConsumoLuminariaGPE(): Promise<void> {
    return new Promise<void>((resolve) => {
      const filter: IFilter<IReporteGenerico> = {
        idsAsignados: this.luminaria?._id, //Acá busca que en idsAsignados esté el id de la luminaria
      };
      if (this.rangoSeleccionado?.inicio && this.rangoSeleccionado?.fin) {
        (filter as any)['valores.timestamp'] = {
          $gte: this.rangoSeleccionado.inicio.toISOString(),
          $lte: this.rangoSeleccionado.fin.toISOString(),
        };
      }
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-valores.timestamp',
        limit: 500,
        includeChildren: true,
        select: 'valores',
      };

      //
      this.reportesConsumoGPE$?.unsubscribe();
      this.reportesConsumoGPE$ = this.lista2
        .reportesGenericos(['Luminaria GPE Energía'], query, 30)
        .subscribe((data) => {
          this.totalCountReporteConsumoGPE = data.totalCount;
          this.reportesConsumoGPE = data.datos;
          this.cdr.detectChanges(); // Forzar detección con OnPush
          resolve();
        });
    });
  }

  public async listarReportesConsumoLuminariaACTIS(): Promise<void> {
    return new Promise<void>((resolve) => {
      const filter: IFilter<IReporteGenerico> = {
        idsAsignados: this.luminaria?._id, //Acá busca que en idsAsignados esté el id de la luminaria
      };
      if (this.rangoSeleccionado?.inicio && this.rangoSeleccionado?.fin) {
        (filter as any)['valores.timestamp'] = {
          $gte: this.rangoSeleccionado.inicio.toISOString(),
          $lte: this.rangoSeleccionado.fin.toISOString(),
        };
      }
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-valores.timestamp',
        limit: 500,
        includeChildren: true,
        select: 'valores',
      };

      //
      this.reportesConsumoACTIS$?.unsubscribe();
      this.reportesConsumoACTIS$ = this.lista2
        .reportesGenericos(['Luminaria ACTIS FING Energía'], query, 30)
        .subscribe((data) => {
          this.totalCountReporteConsumoACTIS = data.totalCount;
          this.reportesConsumoACTIS = data.datos;
          this.cdr.detectChanges(); // Forzar detección con OnPush
          resolve();
        });
    });
  }

  public dataParaGrafico() {
    if (this.luminaria?.dispositivo?.tipo === 'Luminaria Wellness') {
      return this.reportesHistoricoDispositivos;
    } else if (this.luminaria?.dispositivo?.tipo === 'Luminaria GPE') {
      return this.reportesConsumoGPE;
    } else if (this.luminaria?.dispositivo?.tipo === 'Luminaria ACTIS FING') {
      return this.reportesConsumoACTIS;
    } else {
      return null;
    }
  }

  public async filtrarPorRango(rango: { inicio: Date; fin: Date }) {
    this.rangoSeleccionado = rango;

    if (this.luminaria?.dispositivo?.tipo === 'Luminaria Wellness') {
      await this.listarReportesDispositivos();
      this.mapWellnessFechaValores.clear();
      this.reportesHistoricoDispositivos.forEach((item) => {
        if (item.valores?.timestamp) {
          this.mapWellnessFechaValores.set(
            item.valores.timestamp,
            item.valores as IReporteLuminariaWellness,
          );
        }
      });
    } else if (this.luminaria?.dispositivo?.tipo === 'Luminaria GPE') {
      await this.listarReportesConsumoLuminariaGPE();
      this.mapGPEFechaValores.clear();
      this.reportesConsumoGPE.forEach((item) => {
        if (item.valores?.timestamp) {
          this.mapGPEFechaValores.set(
            item.valores.timestamp,
            item.valores as IReporteLuminariaGPEEnergia,
          );
        }
      });
    } else if (this.luminaria?.dispositivo?.tipo === 'Luminaria ACTIS FING') {
      await this.listarReportesConsumoLuminariaACTIS();
      this.mapACTISFechaValores.clear();
      this.reportesConsumoACTIS.forEach((item) => {
        if (item.valores?.timestamp) {
          this.mapACTISFechaValores.set(
            item.valores.timestamp,
            item.valores as IReporteLuminariaACTISEnergia,
          );
        }
      });
    }

    this.hacerGraficosPorKey();
    this.cdr.detectChanges(); // Forzar detección con OnPush
  }

  private async listarDatosParaGrafico() {
    this.tipo = this.luminaria?.dispositivo?.tipo;
    // recién acá ya sabés qué tipo es
    if (this.tipo === 'Luminaria Wellness') {
      await this.listarReportesDispositivos();
    }
    if (this.tipo === 'Luminaria GPE') {
      await this.listarReportesConsumoLuminariaGPE();
    }
    if (this.tipo === 'Luminaria ACTIS FING') {
      await this.listarReportesConsumoLuminariaACTIS();
    }

    if (this.tipo === 'Luminaria Wellness') {
      this.mapWellnessFechaValores.clear(); // Limpiar antes de llenar
      const dataWellness = this.reportesHistoricoDispositivos;
      dataWellness.forEach((item) => {
        if (item.valores?.timestamp) {
          this.mapWellnessFechaValores.set(
            item.valores.timestamp,
            item.valores as IReporteLuminariaWellness,
          );
        }
      });
    } else if (this.tipo === 'Luminaria GPE') {
      this.mapGPEFechaValores.clear(); // Limpiar antes de llenar
      const dataGPE = this.reportesConsumoGPE;
      dataGPE.forEach((item) => {
        if (item.valores?.timestamp) {
          this.mapGPEFechaValores.set(
            item.valores.timestamp,
            item.valores as IReporteLuminariaGPEEnergia,
          );
        }
      });
    } else if (this.tipo === 'Luminaria ACTIS FING') {
      this.mapACTISFechaValores.clear(); // Limpiar antes de llenar
      const dataACTIS = this.reportesConsumoACTIS;
      dataACTIS.forEach((item) => {
        if (item.valores?.timestamp) {
          this.mapACTISFechaValores.set(
            item.valores.timestamp,
            item.valores as IReporteLuminariaACTISEnergia,
          );
        }
      });
    }

    this.hacerGraficosPorKey();
    this.cdr.detectChanges(); // Forzar detección con OnPush
  }

  public cambiarRangoFechas() {
    const dias = Number(this.horasSeleccionadas ?? 1);

    const fin = new Date(); // fecha actual
    const inicio = new Date();
    inicio.setDate(fin.getDate() - dias);

    this.rangoSeleccionado = { inicio, fin };
    this.rangoFechaSeleccionado.emit({ inicio, fin });

    this.filtrarPorRango({ inicio, fin });
  }
  async ngOnInit() {
    this.cambiarRangoFechas();

    await this.listarDatosParaGrafico();
  }

  async ngOnDestroy() {
    this.observadorTamano?.disconnect();
    this.reportesConsumoGPE$?.unsubscribe();
    this.reportesHistoricoDispositivos$?.unsubscribe();
  }
}
